'use client'

import { useEffect, useRef, useState } from 'react'

// Desmos API key. The graded SAT Math surface embeds the same Desmos calculator
// Bluebook uses, so this needs to render reliably.
//
// IMPORTANT (see #775): the key is validated SERVER-SIDE on the script request —
// an invalid key 403s the <script> and `window.Desmos` is never defined (which
// is exactly how the inline calc was failing: it used the bogus key "desmos").
// `dcb31709b452b1cf9dc26972add0fda6` is Desmos's documented DEVELOPMENT demo key
// (returns 200); it's dev-only and commercial use needs a licensed key. So:
// prefer NEXT_PUBLIC_DESMOS_API_KEY (set a licensed key in prod via
// desmos.com/my-api), fall back to the demo key so dev/preview still render.
const DESMOS_DEMO_KEY = 'dcb31709b452b1cf9dc26972add0fda6'
export const DESMOS_API_VERSION = 'v1.12' // current; was pinned to the older v1.10
export const DESMOS_API_KEY = process.env.NEXT_PUBLIC_DESMOS_API_KEY?.trim() || DESMOS_DEMO_KEY

if (
  typeof window !== 'undefined' &&
  process.env.NODE_ENV === 'production' &&
  DESMOS_API_KEY === DESMOS_DEMO_KEY
) {
  console.warn(
    '[Desmos] Using the development demo API key in production. Set NEXT_PUBLIC_DESMOS_API_KEY ' +
      'to a licensed key (desmos.com/my-api) — the demo key is development-only and a paid ' +
      'product needs a license.',
  )
}

export function desmosScriptSrc(): string {
  return `https://www.desmos.com/api/${DESMOS_API_VERSION}/calculator.js?apiKey=${DESMOS_API_KEY}`
}

// Module-scope singleton so the ~4MB script loads at most once per page.
let desmosLoader: Promise<void> | null = null

export function loadDesmosScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve() // SSR no-op
  if ((window as any).Desmos) return Promise.resolve()
  if (desmosLoader) return desmosLoader

  desmosLoader = new Promise<void>((resolve, reject) => {
    const done = (ok: boolean) => {
      if (ok || (window as any).Desmos) resolve()
      else reject(new Error('Desmos failed to load'))
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-desmos="true"]')
    if (existing) {
      existing.addEventListener('load', () => done(true), { once: true })
      existing.addEventListener('error', () => done(false), { once: true })
    } else {
      const s = document.createElement('script')
      s.src = desmosScriptSrc()
      s.async = true
      s.defer = true
      s.setAttribute('data-desmos', 'true')
      s.onload = () => done(true)
      s.onerror = () => done(false)
      document.body.appendChild(s)
    }

    // Safety net: a CSP/network quirk (or a 403 on a bad key) can swallow the
    // error event — resolve if Desmos showed up, reject otherwise.
    setTimeout(() => done(false), 8000)
  }).catch((err) => {
    // Let the next mount retry rather than caching a permanent failure.
    desmosLoader = null
    throw err
  })

  return desmosLoader
}

/**
 * Inline embedded Desmos graphing calculator for the SAT Math section, with a
 * graceful "open in a new tab" fallback if the script can't load. Configured to
 * match the Bluebook graphing surface: the expressions list + on-screen keypad
 * + settings (zoom/display) are all shown (#775).
 */
export default function DesmosCalc() {
  const ref = useRef<HTMLDivElement>(null)
  const calcRef = useRef<any>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    typeof window === 'undefined' ? 'loading' : 'idle',
  )

  useEffect(() => {
    let destroyed = false

    async function init() {
      try {
        setStatus('loading')
        if (typeof window === 'undefined') return

        await loadDesmosScript()
        if (destroyed || !ref.current) return

        const Desmos = (window as any).Desmos
        if (!Desmos) throw new Error('Desmos unavailable after load')

        if (calcRef.current) {
          setStatus('ready')
          return
        }

        // Bluebook parity: full graphing surface (expressions list + keypad +
        // settings), not the stripped-down `expressions:false` config.
        calcRef.current = Desmos.GraphingCalculator(ref.current, {
          expressions: true,
          keypad: true,
          settingsMenu: true,
        })

        if (destroyed) {
          calcRef.current?.destroy?.()
          calcRef.current = null
          return
        }

        setStatus('ready')
      } catch {
        setStatus('error')
      }
    }

    init()

    return () => {
      destroyed = true
      try {
        calcRef.current?.destroy?.()
      } finally {
        calcRef.current = null
      }
    }
  }, [])

  if (status === 'error') {
    return (
      <div className="rounded-md border p-3 text-sm">
        Couldn’t load the inline calculator.{' '}
        <a
          href="https://www.desmos.com/calculator?lang=en"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Open Desmos in a new tab
        </a>
        .
      </div>
    )
  }

  return (
    <div
      className="relative overflow-hidden rounded-md border"
      style={{ height: 'min(72vh, 600px)', minHeight: 420 }}
    >
      {status === 'loading' && (
        <div className="flex h-full items-center justify-center text-sm text-slate-600">
          Loading calculator…
        </div>
      )}
      {status === 'ready' && (
        // Manual clear. State otherwise persists across the whole module, like
        // the real Bluebook calculator (we deliberately do NOT auto-reset per
        // question) — this just gives a one-click wipe when the student wants a
        // fresh slate. setBlank() clears all expressions + resets the viewport.
        <button
          type="button"
          onClick={() => calcRef.current?.setBlank?.()}
          className="absolute right-2 top-2 z-10 rounded border border-slate-300 bg-white/90 px-2 py-1 text-xs font-medium text-slate-700 shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-sky-400"
          aria-label="Clear the calculator"
          title="Clear the calculator"
        >
          Clear
        </button>
      )}
      <div ref={ref} className="h-full w-full" />
    </div>
  )
}
